import { useState, useEffect } from 'react';
import { useTranslation as useI18nTranslation } from 'react-i18next';
import translationService from '../services/translationService';
import useNotification from './useNotification';

/**
 * Custom hook for translation management
 * @returns {Object} Translation management functions and state
 */
const useTranslationManagement = () => {
  const { t, i18n } = useI18nTranslation();
  const { showNotification } = useNotification();
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Load translations list
  const loadTranslations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await translationService.getTranslationsList();
      // Ensure data is an array before setting it
      if (Array.isArray(data)) {
        setTranslations(data);
        return data;
      } else {
        console.error('Expected translations list to be an array but got:', typeof data);
        setTranslations([]);
        return [];
      }
    } catch (err) {
      setError(err.message || 'Error loading translations');
      showNotification({
        type: 'error',
        message: err.message || 'Error loading translations'
      });
      return [];
    } finally {
      setLoading(false);
    }
  };
  
  // Create new translation
  const createTranslation = async (translationData) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await translationService.createTranslation(translationData);
      
      // Reload translations to keep the list current
      await loadTranslations();
      
      showNotification({
        type: 'success',
        message: 'Translation created successfully'
      });
      
      return result;
    } catch (err) {
      setError(err.message || 'Error creating translation');
      showNotification({
        type: 'error',
        message: err.message || 'Error creating translation'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Update translation
  const updateTranslation = async (id, translationData) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await translationService.updateTranslation(id, translationData);
      
      // Reload translations to keep the list current
      await loadTranslations();
      
      showNotification({
        type: 'success',
        message: 'Translation updated successfully'
      });
      
      return result;
    } catch (err) {
      setError(err.message || 'Error updating translation');
      showNotification({
        type: 'error',
        message: err.message || 'Error updating translation'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Delete translation
  const deleteTranslation = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      await translationService.deleteTranslation(id);
      
      // Reload translations to keep the list current
      await loadTranslations();
      
      showNotification({
        type: 'success',
        message: 'Translation deleted successfully'
      });
      
      return true;
    } catch (err) {
      setError(err.message || 'Error deleting translation');
      showNotification({
        type: 'error',
        message: err.message || 'Error deleting translation'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Generate JSON files
  const generateJsonFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await translationService.generateJsonFiles();
      
      showNotification({
        type: 'success',
        message: `JSON files generated successfully for ${result.languages.join(', ')}`
      });
      
      return result;
    } catch (err) {
      setError(err.message || 'Error generating JSON files');
      showNotification({
        type: 'error',
        message: err.message || 'Error generating JSON files'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Import translations from JSON
  const importTranslationsFromJson = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await translationService.importTranslationsFromJson();
      
      // Reload translations after import
      await loadTranslations();
      
      showNotification({
        type: 'success',
        message: `Imported ${result.created} new and updated ${result.updated} translations for ${result.languages.join(', ')}`
      });
      
      return result;
    } catch (err) {
      setError(err.message || 'Error importing translations');
      showNotification({
        type: 'error',
        message: err.message || 'Error importing translations'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Load translations on first mount
  useEffect(() => {
    loadTranslations();
  }, []);
  
  return {
    t,
    i18n,
    translations,
    loading,
    error,
    loadTranslations,
    createTranslation,
    updateTranslation,
    deleteTranslation,
    generateJsonFiles,
    importTranslationsFromJson
  };
};

export default useTranslationManagement;